import React, { useCallback } from 'react';

import PropTypes from 'prop-types';

import { LabelTag } from '@iris/react-components';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { CloseButton, Link, PopoverSheet, Text } from 'reaxl';

import { KeywordSearchTrigger } from './KeywordSearchModalContainer';

function ShoppingAssistantPopover({
    containerElement = null,
    handleAssistantClose = () => { },
    headingText = 'What would you like to do?',
    show = false,
    suggestedActions = null,
    ...rest
}) {

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const {
        ai_assistant_entry_point: [isAIAssistantEntryPointEnabled, { destination: aiEndpoint }],
    } = useFeatures(['ai_assistant_entry_point']);

    const onHide = useCallback(() => {
        handleAssistantClose();
    }, [handleAssistantClose]);

    return (
        <PopoverSheet
            role="menu"
            show={show}
            containerElement={containerElement}
            hideCTA
            heading={headingText}
            onHide={onHide}
            data-cmp="shopping-assistant-popover"
            popoverTriggerId="shopping-assistant-entry"
            id="shopping-assistant-popover"
            placement="top"
            {...rest}
        >
            <div className="display-flex flex-column flex-grow gap-3">
                {!isXs && (
                    <div className="display-flex justify-content-between align-items-center">
                        <Text
                            data-cmp="shopping-assist-dt-heading"
                            color="primary"
                            size={500}
                            weight="ultra-bold"
                        >
                            {headingText}
                        </Text>
                        <CloseButton onClick={handleAssistantClose} />
                    </div>
                )}

                <div className="display-flex flex-wrap">
                    {suggestedActions}
                    {isAIAssistantEntryPointEnabled && (
                        <div className="margin-top-3 margin-horizontal-1">
                            <Link
                                className="text-bold margin-right-2 text-size-200"
                                href={`/${aiEndpoint}`}
                                label="Let AI drive your car search. Ask Autotrader."
                                undecorated
                                target="_blank"
                            />
                            <LabelTag text="BETA" />
                        </div>
                    )}
                </div>

                <hr style={{ width: '100%' }} />

                <KeywordSearchTrigger
                    isShoppingAssistant
                    onTriggerClick={handleAssistantClose}
                />
            </div>
        </PopoverSheet>
    );
}

ShoppingAssistantPopover.propTypes = {
    /**
     * if true, the component will be shown
     * @default false
     */
    show: PropTypes.bool,
    /**
     * An html element that is the container of the sheet
     */
    containerElement: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
    /**
     * A group of CTA's to be rendered in the popover
     */
    suggestedActions: PropTypes.node,
    /**
     * The text to be displayed in the header of the popover
     */
    headingText: PropTypes.string,
};

export default React.memo(ShoppingAssistantPopover, _isEqual);
